import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AdminNav from "@/components/AdminNav";
import "@/styles/portfolio.css";
import "@/styles/admin.css";

const EMPTY = {
  quote: "",
  author: "",
  title: "",
  stars: 5,
  order: 0,
  published: true,
};

export default function AdminTestimonials() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // id or "new"
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/testimonials");
      setItems(data);
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };
  const startEdit = (it) => {
    setEditing(it.id);
    setForm({
      quote: it.quote || "",
      author: it.author || "",
      title: it.title || "",
      stars: it.stars || 5,
      order: it.order || 0,
      published: it.published !== false,
    });
  };
  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (editing === "new") {
        await api.post("/admin/testimonials", form);
      } else {
        await api.patch(`/admin/testimonials/${editing}`, form);
      }
      await load();
      cancel();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Rezension von „${it.author}" löschen?`)) return;
    try {
      await api.delete(`/admin/testimonials/${it.id}`);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="admin-shell" data-testid="admin-testimonials">
      <header className="admin-header">
        <div>
          <span className="eyebrow">ADMIN · REZENSIONEN</span>
          <h1 className="display admin-h1">
            Deine <span className="italic">Kundenstimmen</span>.
          </h1>
        </div>
        <div className="admin-header__actions">
          <button type="button" onClick={startNew} className="btn btn--solid" data-testid="testimonial-new">
            <span>Neue Rezension</span>
            <span className="btn__arrow">→</span>
          </button>
          <button type="button" onClick={handleLogout} className="btn btn--ghost">
            <span>Abmelden</span>
          </button>
        </div>
      </header>

      <AdminNav active="/admin/testimonials" />

      {error && <div className="admin-error">{error}</div>}

      {editing && (
        <form onSubmit={submit} className="admin-form" data-testid="testimonial-form">
          <label className="admin-field">
            <span className="admin-label">Zitat</span>
            <textarea
              className="admin-input admin-textarea"
              rows={4}
              value={form.quote}
              onChange={(e) => setForm({ ...form, quote: e.target.value })}
              required
              data-testid="testimonial-quote"
            />
          </label>
          <label className="admin-field">
            <span className="admin-label">Autor</span>
            <input
              type="text"
              className="admin-input"
              value={form.author}
              onChange={(e) => setForm({ ...form, author: e.target.value })}
              required
              placeholder="z. B. Max Mustermann"
              data-testid="testimonial-author"
            />
          </label>
          <label className="admin-field">
            <span className="admin-label">Titel / Unternehmen</span>
            <input
              type="text"
              className="admin-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="z. B. Inhaber, Café Sonne — Mödling"
              data-testid="testimonial-title"
            />
          </label>
          <div className="admin-row-two">
            <label className="admin-field">
              <span className="admin-label">Sterne (1–5)</span>
              <input
                type="number"
                min={1}
                max={5}
                className="admin-input"
                value={form.stars}
                onChange={(e) => setForm({ ...form, stars: Number(e.target.value) })}
                data-testid="testimonial-stars"
              />
            </label>
            <label className="admin-field">
              <span className="admin-label">Reihenfolge</span>
              <input
                type="number"
                className="admin-input"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                data-testid="testimonial-order"
              />
            </label>
          </div>
          <label className="admin-toggle">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
            />
            <span>Veröffentlicht</span>
          </label>
          <div className="admin-form-foot">
            <button type="button" className="btn btn--ghost" onClick={cancel}>
              <span>Abbrechen</span>
            </button>
            <button type="submit" disabled={busy} className="btn btn--solid" data-testid="testimonial-save">
              <span>{busy ? "Speichert…" : "Speichern"}</span>
              <span className="btn__arrow">→</span>
            </button>
          </div>
        </form>
      )}

      {!editing && (
        <section className="admin-posts">
          {loading && <p className="admin-empty">Lädt…</p>}
          {!loading && items.length === 0 && (
            <p className="admin-empty">Noch keine Rezensionen — lege deine erste an.</p>
          )}
          <ul className="admin-post-list" data-testid="testimonial-list">
            {items.map((it) => (
              <li key={it.id} className="admin-post-row" data-testid={`testimonial-${it.id}`}>
                <div className="admin-post-row__main">
                  <span className="admin-post-row__date">
                    {it.stars} ★ · Order {it.order}
                    {!it.published && <em className="admin-draft"> · Entwurf</em>}
                  </span>
                  <h2 className="admin-post-row__title">{it.author}</h2>
                  <p className="admin-post-row__excerpt">{it.quote}</p>
                  {it.title && (
                    <span className="admin-hint">{it.title}</span>
                  )}
                </div>
                <div className="admin-post-row__actions">
                  <button className="admin-link admin-link--accent" onClick={() => startEdit(it)}>
                    Bearbeiten
                  </button>
                  <button className="admin-link admin-link--danger" onClick={() => remove(it)}>
                    Löschen
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Link to="/admin" className="admin-back" style={{ marginTop: 32 }}>
        ← Zurück zur Übersicht
      </Link>
    </div>
  );
}
